// Import Core
import React, { useState } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
// Import Libraries
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { fitBounds } from "google-map-react";
import { getBounds } from "geolib";
// Import Components
import FloatingButton from "../ui/buttons/FloatingButton";
import TextButton from "../ui/buttons/TextButton";
// Import Utils
import useSlideInMap from "../../utils/useSlideInMap";
import { getSlug } from "../../utils/getSlug";
import { usePlaceholderImage } from "../../utils/useRandomPlaceholderImage";
import { getCardTagline } from "../../utils/getCardTagline";
// Import Assets
import mapMarker from "../../assets/icons/mapMarker.svg";
// Import Icons
import { FaTimes } from "react-icons/fa";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  OverlayViewF,
} from "@react-google-maps/api";

/* Styled Components */
const MapStyles = styled.div`
  height: 100%;
  width: 100%;

  .map {
    height: 100%;
    > div {
      min-height: inherit;
    }
  }

  h2 {
    margin-bottom: 40px;
  }
  .map_marker {
    margin-top: -37px;
    height: 37px;
    width: 29px;
    cursor: pointer;
  }
  .marker_popup {
    display: none;
  }
  .map-marker:hover {
    .marker_popup {
      display: block;
      clear: both;
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      z-index: 20;
    }
  }

  .infoWindow {
    position: relative;
    bottom: 150px;
    left: calc(327px / -2);
    min-width: 327px;
    width: fit-content;

    z-index: 2;
    gap: 0;
    padding: 12px 24px 12px 12px;
    display: flex;
    background: var(--red);
    box-shadow: var(--box-shadow-small);

    @media (max-width: 992px) {
      min-width: unset;
      width: 156px;
      left: calc(156px / -2);
    }

    &::before {
      content: "";
      border: solid transparent;
      border-color: var(--red);
      border-width: 6px;
      height: 0;
      position: absolute;
      pointer-events: none;
      transform: rotate(45deg);
      width: 0;
      z-index: -1;
      left: 50%;
      margin-left: -5px;
      bottom: -5px;
    }

    .close-infoWindow {
      position: absolute;
      top: 8px;
      right: 8px;
      color: var(--white);
      z-index: 2;
      cursor: pointer;
    }
    .card {
      display: flex;
      align-items: flex-start;
      gap: 20px;
      @media (max-width: 992px) {
        gap: 12px;
        flex-direction: column;
      }
      .gatsby-image-wrapper,
      img {
        width: 130px;
        height: 130px;
        min-height: unset;
      }
      .card-body {
        display: flex;
        padding: 8px 0px;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
        color: var(--white);
        .card-heading {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          .tagline {
            color: var(--red-25);
          }
        }
        a {
          span {
            color: var(--white);
          }
        }
      }
    }
  }
`;

// InfoWindow component
const InfoWindow = ({ page }) => {
  const pageSlug = getSlug(page);

  return (
    <div className="infoWindow">
      <div className="close-infoWindow">
        <FaTimes />
      </div>

      <div className="card">
        {page.thumbnail && page.thumbnail.asset && (
          <GatsbyImage image={getImage(page.thumbnail.asset)} alt={page.name} />
        )}

        <div className="card-body">
          <div className="card-heading">
            <p className="tagline">
              {page._type === "countryPage"
                ? page.isInEurope === "europe"
                  ? "Europe"
                  : "Further Afield"
                : getCardTagline(page._type)}
            </p>
            {page.name ? (
              <h5 className="alt card-title">{page.name}</h5>
            ) : page.title ? (
              <h5 className="alt card-title">{page.title}</h5>
            ) : null}
          </div>
          <Link to={pageSlug} className="read-more-btn">
            <TextButton
              size="sm"
              className=""
              value={"More Info"}
              clickHandler={false}
              isDisabled={false}
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

/* Components */
const AnyReactComponent = ({
  page,
  show,
  setClickedPage,
  placeholderImage,
}) => {
  return (
    <div onClick={() => setClickedPage(page._id)} className="map-marker">
      <img
        style={{
          width: 29,
          height: 37,
          position: "absolute",
          transform: "translate(-50%, -100%)",
        }}
        src={mapMarker}
        alt={page.name}
      />

      {show === page._id && (
        <InfoWindow page={page} placeholderImage={placeholderImage} />
      )}
      <div
        className="marker_popup"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          background: "#ffffff",
          padding: "3px 5px",
          borderRadius: "3px",
        }}
      >
        {page.name ? page.name : page.title}
      </div>
    </div>
  );
};

/* Render Component */
const MapMultipleLocations = ({
  locations,
  mapItems,
  filterLocation,
  isActiveMenuItem,
  showMenu,
}) => {
  const [show, setShow] = useState(null);
  const [isMapOpen, toggleMap] = useSlideInMap();
  const { randomPlaceholderImage } = usePlaceholderImage();

  const { zoom } = oldMapPluginCalculations(locations);
  const [center, setCenter] = useState(
    oldMapPluginCalculations(locations).center
  );

  const setClickedPage = (key) => {
    setShow(key);
    if (key === show) {
      setShow(null);
    }
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API,
  });

  const containerStyle = {
    width: "100%",
    height: "100%",
  };

  const [map, setMap] = React.useState(null);

  const onLoad = React.useCallback(function callback(map) {
    // const bounds = new window.google.maps.LatLngBounds(center);
    map.setZoom(zoom);
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  const hideMShowMarkers = (opacity) => {
    const markers = document.querySelectorAll(".map-marker");
    markers.forEach((marker) => {
      marker.style.opacity = opacity;
    });
  };

  return (
    <MapStyles className="slide-in-map">
      <div className={`map ${mapItems.length >= 3 ? "map-padding" : null}`}>
        {mapItems && mapItems.length > 0 && showMenu && (
          <nav
            className={`map-menu ${
              mapItems.length >= 3 ? "outside-map" : null
            }`}
          >
            <p
              className={`map-menu-item${
                isActiveMenuItem === null ? " active" : ""
              }`}
              onClick={() => filterLocation(null)}
            >
              All
            </p>
            {mapItems.map((location, index) => {
              return (
                <p
                  className={`map-menu-item${
                    isActiveMenuItem === index ? " active" : ""
                  }`}
                  key={index}
                  onClick={() => filterLocation(index)}
                >
                  {location.name}
                </p>
              );
            })}
          </nav>
        )}
        <FloatingButton
          className="slide-in-map-btn"
          value="Close Map"
          clickHandler={toggleMap}
        />
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onDragStart={() => hideMShowMarkers(0)}
            onDragEnd={() => hideMShowMarkers(1)}
            options={{
              mapTypeControl: false,
              mapId: "5859430cbbd48a11",
            }}
          >
            {locations.map((page, index) => (
              <OverlayViewF
                position={page.location}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <AnyReactComponent
                  key={`${page._id}-${index}`}
                  page={page}
                  show={show}
                  setClickedPage={setClickedPage}
                  placeholderImage={randomPlaceholderImage}
                />
              </OverlayViewF>
            ))}
            <></>
          </GoogleMap>
        )}
      </div>
    </MapStyles>
  );
};

const oldMapPluginCalculations = (locations) => {
  const pagesLatLng = locations.map((page) => page.location);

  const getPagesBounds = getBounds(pagesLatLng);
  const bounds = {
    ne: {
      lat: getPagesBounds.maxLat,
      lng: getPagesBounds.maxLng,
    },
    sw: {
      lat: getPagesBounds.minLat,
      lng: getPagesBounds.minLng,
    },
  };
  const size = {
    width: 500,
    height: 650,
  };

  const { center, zoom } = fitBounds(bounds, size);
  if (locations.length === 1) {
    center.lat = locations[0].location.lat;
    center.lng = locations[0].location.lng;
  }

  return { center, zoom };
};

export default MapMultipleLocations;
